import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Phone, Mail, ChevronDown, Menu, X, MapPin } from "lucide-react";
import { Link, useNavigate } from "@/lib/router-compat";
import { ScottyLogo } from "./ScottyLogo";
import { QuoteModal } from "./QuoteModal";
import { services } from "../data/services";
import { groupACities, groupBCities } from "../data/serviceAreas";
import { PHONE_DISPLAY, PHONE_LINK, EMAIL, EMAIL_LINK } from "../data/business";

export const serviceCategories = [
  {
    name: "Main Services",
    items: services.map((s) => ({ name: s.name, slug: s.slug })),
  },
];

export const Header: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [mobileMenuMounted, setMobileMenuMounted] = useState(false);
  const [mobileSection, setMobileSection] = useState<string | null>(null);
  const closeTimer = useRef<number | null>(null);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const navigate = useNavigate();

  const openMobile = () => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setMobileMenuVisible(true);
    setMobileMenuOpen(true);
  };

  const closeMobile = () => {
    setMobileMenuOpen(false);
    setMobileSection(null);
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (reduceMotion) {
      setMobileMenuVisible(false);
      return;
    }
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => {
      setMobileMenuVisible(false);
      closeTimer.current = null;
    }, 280);
  };

  useEffect(() => {
    setMobileMenuMounted(true);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileMenuVisible ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
      if (closeTimer.current) window.clearTimeout(closeTimer.current);
    };
  }, [mobileMenuVisible]);

  const go = (path: string) => {
    closeMobile();
    setOpenDropdown(null);
    navigate(path);
  };

  const toggleMobileSection = (section: string) => {
    setMobileSection((current) => (current === section ? null : section));
  };

  return (
    <>
      <header className="sticky top-0 z-50 hidden shadow-md md:block">
        {/* Top bar - charcoal */}
        <div className="bg-[#252A2B] text-white text-xs font-semibold py-2 px-4 md:px-10 border-b border-white/10">
          <div className="max-w-[1320px] mx-auto w-full flex justify-between items-center">
            <div className="flex items-center gap-6">
              <a
                href={PHONE_LINK}
                className="flex items-center gap-1.5 hover:text-[#D96B27] transition-colors"
              >
                <Phone className="w-3.5 h-3.5" />
                <span>{PHONE_DISPLAY}</span>
              </a>
              <a
                href={EMAIL_LINK}
                className="hidden sm:flex items-center gap-1.5 hover:text-[#D96B27] transition-colors"
              >
                <span>{EMAIL}</span>
              </a>
            </div>
            <div className="hidden sm:block">
              <span>Based in Highland · Serving Utah County</span>
            </div>
          </div>
        </div>

        {/* Main navigation */}
        <div className="bg-[#F8F6F1] text-[#252A2B] border-b border-[#E9E6DF]">
          <div className="max-w-[1320px] mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center gap-4 py-2.5">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center shrink-0 group max-h-16 lg:max-h-20"
            >
              <ScottyLogo
                size="sm"
                className="drop-shadow-md transition-transform duration-200 group-hover:scale-105 max-h-12 lg:max-h-14"
              />
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-3 lg:gap-5 text-xs lg:text-[13px] font-semibold text-[#252A2B] whitespace-nowrap">
              <button
                onClick={() => go("/")}
                className="hover:text-[#D96B27] transition-colors py-1 cursor-pointer uppercase tracking-wide"
              >
                Home
              </button>

              {/* Services Dropdown */}
              <div
                className="relative group py-1"
                onMouseEnter={() => setOpenDropdown("services")}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <button className="flex items-center gap-1 hover:text-[#D96B27] transition-colors cursor-pointer uppercase tracking-wide">
                  Services
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                {openDropdown === "services" && (
                  <div className="absolute left-0 top-full pt-2 w-64 z-50">
                    <div className="bg-[#252A2B] border border-white/10 rounded-xl shadow-2xl py-2 px-1 text-white">
                      {services.map((s) => (
                        <button
                          key={s.slug}
                          onClick={() => go(`/services/${s.slug}`)}
                          className="w-full text-left block px-3 py-2 text-xs text-white/90 hover:text-white hover:bg-[#D96B27] rounded-md transition-colors whitespace-normal"
                        >
                          {s.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Service Areas Dropdown */}
              <div
                className="relative group py-1"
                onMouseEnter={() => setOpenDropdown("areas")}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <button className="flex items-center gap-1 hover:text-[#D96B27] transition-colors cursor-pointer uppercase tracking-wide">
                  <MapPin className="w-3.5 h-3.5 text-[#D96B27]" />
                  Service Areas
                  <ChevronDown className="w-3.5 h-3.5" />
                </button>
                {openDropdown === "areas" && (
                  <div className="absolute left-0 top-full pt-2 w-72 z-50">
                    <div className="service-areas-scroll bg-[#252A2B] border border-white/10 rounded-xl shadow-2xl py-2 px-1 text-white max-h-[80vh] overflow-y-auto">
                      <div className="px-3 pt-1 pb-1 text-[10px] font-bold uppercase tracking-wider text-white/40">
                        All Services
                      </div>
                      {groupACities.map((c) => (
                        <button
                          key={c.slug}
                          onClick={() => go(`/service-areas/${c.slug}`)}
                          className="w-full text-left block px-3 py-2 text-xs text-white/90 hover:text-white hover:bg-[#D96B27] rounded-md transition-colors whitespace-normal"
                        >
                          {c.name}, UT
                        </button>
                      ))}
                      <div className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-white/40">
                        Weed Removal
                      </div>
                      {groupBCities.map((c) => (
                        <button
                          key={c.slug}
                          onClick={() => go(`/service-areas/${c.slug}`)}
                          className="w-full text-left block px-3 py-2 text-xs text-white/90 hover:text-white hover:bg-[#D96B27] rounded-md transition-colors whitespace-normal"
                        >
                          {c.name}, UT
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={() => go("/about")}
                className="hover:text-[#D96B27] transition-colors py-1 cursor-pointer uppercase tracking-wide"
              >
                About
              </button>
              <button
                onClick={() => go("/blog")}
                className="hover:text-[#D96B27] transition-colors py-1 cursor-pointer uppercase tracking-wide"
              >
                Blog
              </button>
              <button
                onClick={() => go("/contact")}
                className="hover:text-[#D96B27] transition-colors py-1 cursor-pointer uppercase tracking-wide"
              >
                Contact
              </button>

              <div className="flex items-start gap-3 ml-2">
                <div className="flex flex-col items-center">
                  <a
                    href={PHONE_LINK}
                    className="bg-[#D96B27] border-2 border-[#D96B27] text-white hover:bg-[#BA541B] hover:border-[#BA541B] px-3.5 py-1.5 rounded-full font-bold text-xs flex items-center gap-1.5 transition-all shadow-md hover:scale-[1.02] uppercase tracking-wide"
                  >
                    <Phone className="w-3.5 h-3.5" />
                    <span>{PHONE_DISPLAY}</span>
                  </a>
                  <span className="text-[8px] font-bold uppercase tracking-[0.15em] mt-0.5 text-[#252A2B]/80 text-center">
                    We text 24/7
                  </span>
                </div>
                <button
                  onClick={() => setQuoteModalOpen(true)}
                  className="bg-white border-2 border-[#252A2B] text-[#252A2B] hover:bg-[#252A2B] hover:text-white px-3.5 py-1.5 rounded-full font-bold text-xs transition-all shadow-md hover:scale-[1.02] uppercase tracking-wide"
                >
                  Get Free Quote
                </button>
              </div>
            </nav>
          </div>
        </div>
      </header>

      {mobileMenuMounted &&
        createPortal(
          <div className="md:hidden">
            {mobileMenuVisible && (
              <button
                type="button"
                aria-label="Close menu"
                className={`mobile-menu-backdrop fixed inset-0 bg-[#252A2B]/50${mobileMenuOpen ? "" : " is-closing"}`}
                style={{ zIndex: 2147483000 }}
                onClick={closeMobile}
              />
            )}
            {mobileMenuVisible && (
              <div
                className={`mobile-menu-scroll mobile-menu-sheet fixed inset-x-0 bottom-20 max-h-[70vh] overflow-y-auto rounded-t-2xl bg-[#252A2B] px-4 pt-3 pb-1 text-white${mobileMenuOpen ? "" : " is-closing"}`}
                style={{ zIndex: 2147483001 }}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 space-y-0.5 text-[13px] leading-tight">
                    <a
                      href={PHONE_LINK}
                      className="flex items-center gap-2 text-white"
                    >
                      <Phone className="h-3.5 w-3.5 shrink-0" />
                      {PHONE_DISPLAY}
                    </a>
                    <a
                      href={EMAIL_LINK}
                      className="flex items-center gap-2 break-all text-white"
                    >
                      <Mail className="h-3.5 w-3.5 shrink-0" />
                      {EMAIL}
                    </a>
                    <p className="text-[11px] leading-tight text-white/80">
                      Based in Highland · Serving Utah County
                    </p>
                  </div>
                  <Link to="/" onClick={closeMobile} className="shrink-0">
                    <ScottyLogo size="sm" className="[&_img]:!h-12" />
                  </Link>
                </div>
                <Link
                  to="/"
                  onClick={closeMobile}
                  className="flex w-full items-center border-b border-white/10 pt-1 pb-2 text-left font-medium uppercase tracking-wide text-white"
                >
                  Home
                </Link>

                <div className="border-b border-white/10">
                  <button
                    type="button"
                    onClick={() => toggleMobileSection("services")}
                    className="flex h-12 w-full items-center justify-between text-left font-medium uppercase tracking-wide text-white"
                    aria-expanded={mobileSection === "services"}
                  >
                    Services
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${mobileSection === "services" ? "rotate-180" : ""}`}
                    />
                  </button>
                  {mobileSection === "services" && (
                    <div className="space-y-1 pb-2">
                      {services.map((s) => (
                        <Link
                          key={s.slug}
                          to={`/services/${s.slug}`}
                          onClick={closeMobile}
                          className="block py-1 pl-3 text-sm text-white/80 hover:text-white"
                        >
                          {s.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                <div className="border-b border-white/10">
                  <button
                    type="button"
                    onClick={() => toggleMobileSection("areas")}
                    className="flex h-12 w-full items-center justify-between text-left font-medium uppercase tracking-wide text-white"
                    aria-expanded={mobileSection === "areas"}
                  >
                    Service Areas
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${mobileSection === "areas" ? "rotate-180" : ""}`}
                    />
                  </button>
                  {mobileSection === "areas" && (
                    <div className="space-y-1 pb-2">
                      <div className="pl-3 pt-1 pb-1 text-[10px] font-bold uppercase tracking-wider text-white/40">
                        All Services
                      </div>
                      {groupACities.map((c) => (
                        <Link
                          key={c.slug}
                          to={`/service-areas/${c.slug}`}
                          onClick={closeMobile}
                          className="block py-1 pl-3 text-sm text-white/80 hover:text-white"
                        >
                          {c.name}, UT
                        </Link>
                      ))}
                      <div className="pl-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wider text-white/40">
                        Weed Removal
                      </div>
                      {groupBCities.map((c) => (
                        <Link
                          key={c.slug}
                          to={`/service-areas/${c.slug}`}
                          onClick={closeMobile}
                          className="block py-1 pl-3 text-sm text-white/80 hover:text-white"
                        >
                          {c.name}, UT
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                <Link
                  to="/about"
                  onClick={closeMobile}
                  className="flex h-12 w-full items-center border-b border-white/10 text-left font-medium uppercase tracking-wide text-white"
                >
                  About
                </Link>
                <Link
                  to="/blog"
                  onClick={closeMobile}
                  className="flex h-12 w-full items-center border-b border-white/10 text-left font-medium uppercase tracking-wide text-white"
                >
                  Blog
                </Link>
                <Link
                  to="/contact"
                  onClick={closeMobile}
                  className="flex h-12 w-full items-center text-left font-medium uppercase tracking-wide text-white"
                >
                  Contact
                </Link>
              </div>
            )}

            <div
              className="fixed inset-x-0 bottom-0 bg-[#F8F6F1] px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
              style={{ zIndex: 2147483002 }}
            >
              <div className="mx-auto grid max-w-lg grid-cols-[3.25rem_1fr_3.25rem] items-center gap-3">
                <button
                  type="button"
                  onClick={() => (mobileMenuOpen ? closeMobile() : openMobile())}
                  className="flex h-12 w-12 items-center justify-center rounded-full border border-[#E9E6DF] bg-white text-[#252A2B] shadow-sm"
                  aria-label={mobileMenuVisible ? "Close menu" : "Open menu"}
                  aria-expanded={mobileMenuOpen}
                >
                  {mobileMenuVisible ? (
                    <X className="h-6 w-6" />
                  ) : (
                    <Menu className="h-6 w-6" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    closeMobile();
                    setQuoteModalOpen(true);
                  }}
                  className="h-14 rounded-full bg-[#D96B27] text-sm font-bold uppercase tracking-wide text-white shadow-md"
                >
                  Get Free Quote
                </button>
                <a
                  href={PHONE_LINK}
                  className="flex h-12 w-12 items-center justify-center rounded-full bg-[#252A2B] text-white shadow-md"
                  aria-label={`Call ${PHONE_DISPLAY}`}
                >
                  <Phone className="h-5 w-5" />
                </a>
              </div>
            </div>
          </div>,
          document.body,
        )}

      <QuoteModal
        isOpen={quoteModalOpen}
        onClose={() => setQuoteModalOpen(false)}
      />
    </>
  );
};
